/**
 * MH-32 Báo cáo định kỳ (đợt 3 phần C2), dựng lại theo bản mẫu CMS "Thông tin
 * nguồn": "Quản lý báo cáo thông tin cơ sở" (`/bao-cao`, cheDo 'quan-ly') và
 * "Nhận duyệt gửi báo cáo" (`/bao-cao/nhan-duyet`, cheDo 'nhan-duyet').
 *
 * Danh sách kỳ báo cáo + chi tiết một kỳ (`/bao-cao/:id`), gộp trong một tệp vì
 * chi tiết chỉ dùng lại trong phạm vi màn hình này.
 *
 * Vòng đời bắt buộc theo phân công: tạo kỳ → tự tổng hợp → điều chỉnh (LÝ DO BẮT
 * BUỘC) → chốt kỳ (khoá chỉ đọc) → mở lại (quyền riêng `report.reopen`, ghi nhật
 * ký). Bảng chỉ tiêu LUÔN hiện đủ ba cột: hệ thống tự tính (không sửa được),
 * điều chỉnh, giá trị chốt — không được gộp ba cột này lại.
 *
 * Ánh xạ bản mẫu → mô hình dữ liệu hiện có: bản mẫu liệt kê mỗi dòng là một đơn
 * vị hành chính cấp xã kèm số liệu chỉ tiêu; backend hiện chỉ có "kỳ báo cáo" cấp
 * tỉnh (mã báo cáo, quý, năm, trạng thái) và số liệu nằm trong chi tiết kỳ. Cột
 * và bộ lọc không có dữ liệu được khoá/để "—" kèm dải "Chưa có API".
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import {
  baoCaoApi,
  thongKeApi,
  type ChiTietKyBaoCao,
  type DongChiTieu,
  type DongKyBaoCao,
} from '../api/bao-cao-api';
import { LoiApi } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';
import { DaiChuaCoApi, NhanChuaCoApi, TheTag, type KieuTag } from '../thanh-phan/MauGiaoDien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import './quan-tri.css';

const t = viQuanTri.baoCao;

/* ------------------------------ Dùng chung ------------------------------- */

const KIEU_TAG_TRANG_THAI: Record<DongKyBaoCao['trangThai'], KieuTag> = {
  nhap: 'trung-tinh',
  da_duyet: 'ok',
  da_gui: 'xanh',
};

function TagTrangThaiKy({ trangThai }: { trangThai: DongKyBaoCao['trangThai'] }): ReactNode {
  return <TheTag kieu={KIEU_TAG_TRANG_THAI[trangThai]}>{vi.baoCaoDinhKy.trangThai[trangThai]}</TheTag>;
}

const dinhDangThoiDiem = (s: string | null): string => (s ? new Date(s).toLocaleString('vi-VN') : '—');

/** Các năm cho bộ lọc "Năm": năm hiện tại lùi 4 năm, cộng 1 năm tới. */
function cacNam(): number[] {
  const nay = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => nay + 1 - i);
}

/**
 * Việc xuất một kỳ báo cáo ra tệp (POST /api/v1/reports/export, loaiBaoCao
 * `tong_hop_ky`) — chạy nền, hỏi trạng thái định kỳ đến khi xong.
 */
function dungXuatKy(api: Pick<typeof thongKeApi, 'xuat' | 'trangThaiXuat'>): {
  dangXuat: boolean;
  url: string | null;
  loi: string | null;
  xuat: (kyId: string) => Promise<void>;
} {
  const [dangXuat, datDangXuat] = useState(false);
  const [url, datUrl] = useState<string | null>(null);
  const [loi, datLoi] = useState<string | null>(null);
  const hen = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (hen.current) clearInterval(hen.current);
    },
    [],
  );

  const xuat = async (kyId: string): Promise<void> => {
    datLoi(null);
    datUrl(null);
    datDangXuat(true);
    try {
      const { id } = await api.xuat({ loaiBaoCao: 'tong_hop_ky', dinhDang: 'xlsx', kyId });
      if (hen.current) clearInterval(hen.current);
      hen.current = setInterval(() => {
        void api
          .trangThaiXuat(id)
          .then((tt) => {
            if (tt.trangThai !== 'thanh_cong' && tt.trangThai !== 'that_bai') return;
            if (hen.current) clearInterval(hen.current);
            datDangXuat(false);
            if (tt.urlTaiVe) datUrl(tt.urlTaiVe);
            if (tt.trangThai === 'that_bai') datLoi(tt.thongBaoLoi ?? t.xuatLoi);
          })
          .catch((err: unknown) => {
            if (hen.current) clearInterval(hen.current);
            datDangXuat(false);
            datLoi(err instanceof LoiApi ? err.message : t.xuatLoi);
          });
      }, 1500);
    } catch (err) {
      datDangXuat(false);
      datLoi(err instanceof LoiApi ? err.message : t.xuatLoi);
    }
  };

  return { dangXuat, url, loi, xuat };
}

/**
 * Khối lọc báo cáo theo mẫu ReportFilter: hàng trên là loại báo cáo (trái) và
 * nút hành động (phải); hàng dưới là bốn nhóm lọc có nhãn nhóm phía trên ô nền
 * group-bg. Backend chỉ lọc được theo NĂM — các nhóm còn lại bị khoá.
 */
function BoLocBaoCao({
  tienTo,
  nam,
  onDoiNam,
  nutPhai,
}: {
  tienTo: string;
  nam: string;
  onDoiNam: (nam: string) => void;
  nutPhai: ReactNode;
}): ReactNode {
  const id = (k: string): string => `${tienTo}-${k}`;
  return (
    <>
      <div className="qt-dau">
        <span>
          <select
            id={id('loai')}
            className="qt-select qt-select--loai-bao-cao"
            aria-label={t.loaiBaoCao}
            disabled
            defaultValue={t.cacLoaiBaoCao[0]}
          >
            {t.cacLoaiBaoCao.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <NhanChuaCoApi />
        </span>
        <div className="qt-dau__phai">{nutPhai}</div>
      </div>

      <div className="nhom-loc" data-vung="bo-loc-bao-cao">
        <div className="nhom-loc__muc">
          <span className="nhom-loc__nhan" id={id('nhan-dv')}>
            {t.nhom.donViHanhChinh} <NhanChuaCoApi />
          </span>
          <div className="nhom-loc__o nhom-loc__o--don-vi" role="group" aria-labelledby={id('nhan-dv')}>
            <div className="nhom-loc__chon">
              {[t.loaiDonVi.phuong, t.loaiDonVi.xa, t.loaiDonVi.dacKhu].map((l) => (
                <label key={l}>
                  <input type="checkbox" defaultChecked disabled /> {l}
                </label>
              ))}
            </div>
            <select className="qt-select" aria-label={t.nhom.donViHanhChinh} disabled>
              <option>{t.tatCa}</option>
            </select>
          </div>
        </div>
        <div className="nhom-loc__muc">
          <label className="nhom-loc__nhan" htmlFor={id('quy')}>
            {t.nhom.quy} <NhanChuaCoApi />
          </label>
          <div className="nhom-loc__o">
            <select id={id('quy')} className="qt-select" disabled>
              <option>{t.tatCa}</option>
              {[1, 2, 3, 4].map((q) => (
                <option key={q}>{q}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="nhom-loc__muc">
          <label className="nhom-loc__nhan" htmlFor={id('nam')}>
            {t.nhom.nam}
          </label>
          <div className="nhom-loc__o">
            <select
              id={id('nam')}
              className="qt-select"
              value={nam}
              onChange={(e) => onDoiNam(e.target.value)}
            >
              <option value="">{t.tatCa}</option>
              {cacNam().map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="nhom-loc__muc">
          <label className="nhom-loc__nhan" htmlFor={id('quy-trinh')}>
            {t.nhom.quyTrinh} <NhanChuaCoApi />
          </label>
          <div className="nhom-loc__o">
            <select id={id('quy-trinh')} className="qt-select" disabled>
              <option>{t.tatCa}</option>
              {(['nhap', 'da_duyet', 'da_gui'] as const).map((s) => (
                <option key={s}>{vi.baoCaoDinhKy.trangThai[s]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------- Danh sách -------------------------------- */

export interface MH32Props {
  api?: Pick<typeof baoCaoApi, 'danhSachKy' | 'taoKy'>;
  apiXuat?: Pick<typeof thongKeApi, 'xuat' | 'trangThaiXuat'>;
  /** Chế độ trang theo menu của bản mẫu CMS: quản lý báo cáo, nhận duyệt gửi báo cáo. */
  cheDo?: 'quan-ly' | 'nhan-duyet';
}

export function MH32BaoCaoDinhKy({ api, apiXuat, cheDo = 'quan-ly' }: MH32Props = {}): ReactNode {
  const goi = api ?? baoCaoApi;
  const dieuHuong = useNavigate();
  const phien = dungPhien();
  const [thamSo, datThamSo] = useSearchParams();
  const [moTao, datMoTao] = useState(false);
  const [maBaoCao, datMaBaoCao] = useState('');
  const [quy, datQuy] = useState(1);
  const [nam, datNam] = useState(new Date().getFullYear());
  const [dangTao, datDangTao] = useState(false);
  const [loiTao, datLoiTao] = useState<string | null>(null);
  const [tab, datTab] = useState<'duyet' | 'tw'>('duyet');
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const xuatKy = dungXuatKy(apiXuat ?? thongKeApi);

  const trang = Number(thamSo.get('page') ?? 1) || 1;
  const namLoc = thamSo.get('nam') ?? '';
  const laNhanDuyet = cheDo === 'nhan-duyet';

  const kq = dungTaiDuLieu<{ items: DongKyBaoCao[]; total: number; page: number; pageSize: number }>({
    tai: (signal) =>
      goi.danhSachKy(
        { page: trang, pageSize: SO_DONG_MAC_DINH, nam: namLoc ? Number(namLoc) : undefined },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: namLoc !== '',
    phuThuoc: [trang, namLoc],
  });

  // Đổi trang, bộ lọc hoặc tab thì bỏ chọn — không giữ lựa chọn "ẩn" ngoài trang.
  useEffect(() => {
    datDaChon(new Set());
  }, [trang, namLoc, tab]);

  const dong = kq.duLieu?.items ?? [];
  const stt = useMemo(
    () => new Map(dong.map((d, i) => [d.id, (trang - 1) * SO_DONG_MAC_DINH + i + 1])),
    [dong, trang],
  );
  const tenDonVi = phien.hoSo?.tenTinh ?? '—';

  const doiThamSo = (k: string, v: string): void => {
    const moi = new URLSearchParams(thamSo);
    if (v) moi.set(k, v);
    else moi.delete(k);
    if (k !== 'page') moi.delete('page');
    datThamSo(moi, { replace: true });
  };

  const cotStt: CotBang<DongKyBaoCao> = { khoa: 'stt', nhan: t.cot.stt, ve: (d) => stt.get(d.id) ?? '' };

  const cotQuanLy: CotBang<DongKyBaoCao>[] = [
    cotStt,
    {
      khoa: 'maBaoCao',
      nhan: t.cot.maDinhDanh,
      ve: (d) => <Link to={`/bao-cao/${d.id}`}>{d.maBaoCao}</Link>,
    },
    { khoa: 'quy', nhan: t.cot.quy, ve: (d) => d.quy },
    { khoa: 'nam', nhan: t.cot.nam, ve: (d) => d.nam },
    { khoa: 'tenDonVi', nhan: t.cot.tenDonVi, ve: () => tenDonVi },
    // Năm cột số liệu: danh sách kỳ không trả giá trị chỉ tiêu (xem DaiChuaCoApi).
    { khoa: 'tongSoThon', nhan: t.cot.tongSoThon, ve: () => '—' },
    { khoa: 'daiFm', nhan: t.cot.daiFm, ve: () => '—' },
    { khoa: 'daiCntt', nhan: t.cot.daiCntt, ve: () => '—' },
    { khoa: 'soCumLoa', nhan: t.cot.soCumLoa, ve: () => '—' },
    { khoa: 'soNguoi', nhan: t.cot.soNguoi, ve: () => '—' },
    { khoa: 'trangThai', nhan: t.cot.quyTrinh, ve: (d) => <TagTrangThaiKy trangThai={d.trangThai} /> },
  ];

  const cotNhanDuyet: CotBang<DongKyBaoCao>[] = [
    cotStt,
    {
      khoa: 'donVi',
      nhan: t.cot.donViHanhChinh,
      ve: (d) => <Link to={`/bao-cao/${d.id}`}>{`${tenDonVi} · ${d.maBaoCao}`}</Link>,
    },
    { khoa: 'quy', nhan: t.cot.quy, ve: (d) => d.quy },
    { khoa: 'nam', nhan: t.cot.nam, ve: (d) => d.nam },
    { khoa: 'guiLuc', nhan: t.cot.thoiGianGui, ve: (d) => dinhDangThoiDiem(d.guiLuc) },
    { khoa: 'trangThai', nhan: t.cot.trangThai, ve: (d) => <TagTrangThaiKy trangThai={d.trangThai} /> },
  ];

  const guiTao = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangTao(true);
    datLoiTao(null);
    try {
      const ky = await goi.taoKy({ maBaoCao, quy, nam });
      datMoTao(false);
      dieuHuong(`/bao-cao/${ky.id}`);
    } catch (err) {
      datLoiTao(err instanceof LoiApi ? err.message : 'Không tạo được kỳ báo cáo');
    } finally {
      datDangTao(false);
    }
  };

  const kyXuat = daChon.size === 1 ? [...daChon][0] : undefined;
  const nutQuanLy = (
    <>
      <button
        type="button"
        className="nut nut--chinh"
        disabled={!kyXuat || xuatKy.dangXuat}
        title={kyXuat ? undefined : t.chonMotKyDeXuat}
        onClick={() => kyXuat && void xuatKy.xuat(kyXuat)}
      >
        {xuatKy.dangXuat ? t.dangXuat : t.xuatBaoCao}
      </button>
      <button type="button" className="nut" disabled>
        {t.nhapBaoCao}
      </button>
      <NhanChuaCoApi />
    </>
  );

  const coChon = daChon.size > 0;
  const nutNhanDuyet =
    tab === 'duyet' ? (
      // Chưa có API "gửi duyệt" (OpenAPI có /reports/periods/{id}/submit, backend
      // chưa hiện thực). KHÔNG dùng tạm API chốt kỳ: chốt sẽ khoá số liệu.
      <>
        <button type="button" id="btn-gd" className={coChon ? 'nut nut--chinh' : 'nut'} disabled>
          {t.guiDuyet}
        </button>
        <NhanChuaCoApi />
      </>
    ) : (
      <>
        <button type="button" id="btn-gd" className={coChon ? 'nut nut--chinh' : 'nut'} disabled>
          {t.guiTrungUong}
        </button>
        <NhanChuaCoApi />
      </>
    );

  const canApi = laNhanDuyet
    ? tab === 'duyet'
      ? [
          'POST /api/v1/reports/periods/{id}/submit (gửi duyệt — đã có trong OpenAPI 1.0.2, backend chưa hiện thực)',
          'GET /api/v1/reports/periods?trangThai=nhap|da_duyet&quy=&donViHanhChinh= (lọc theo quy trình, quý, đơn vị)',
          'GET /api/v1/reports/periods — trả kèm đơn vị hành chính gửi báo cáo (báo cáo cấp xã)',
        ]
      : [
          'POST /api/v1/reports/periods/{id}/submit (đã có trong OpenAPI 1.0.2, backend chưa hiện thực)',
          'GET /api/v1/reports/periods?trangThai=da_duyet|da_gui',
        ]
    : [
        'GET /api/v1/reports/periods?quy=&trangThai=&loaiDonVi=&donViHanhChinh=&maBaoCao=',
        'GET /api/v1/reports/periods — trả kèm giá trị chốt các chỉ tiêu theo đơn vị hành chính (tổng số thôn/tổ dân phố, đài FM, đài CNTT, số cụm loa, số người làm công tác truyền thanh)',
        'GET /api/v1/reports/types (danh mục loại báo cáo)',
        'POST /api/v1/reports/periods/import (nhập báo cáo từ tệp)',
      ];

  return (
    <div className="man-hinh man-hinh--bao-cao" data-che-do={cheDo}>
      <header className="man-hinh__dau">
        <h1>{laNhanDuyet ? vi.menuCon.nhanDuyetGuiBaoCao : vi.menuCon.baoCaoDinhKy}</h1>
        <div className="man-hinh__hanh-dong">
          {/* /thong-ke (MH-33) chưa có mục điều hướng riêng (giữ nguyên số mục
              điều hướng hiện có của khung trang) — vào từ đây hoặc đường dẫn. */}
          <Link to="/thong-ke" className="nut">
            {vi.thongKe.tieuDe}
          </Link>
          {!laNhanDuyet ? (
            <button type="button" className="nut" onClick={() => datMoTao(true)}>
              {vi.baoCaoDinhKy.taoKyMoi}
            </button>
          ) : null}
        </div>
      </header>

      {laNhanDuyet ? (
        <div className="tab" role="tablist">
          {(
            [
              ['duyet', t.tab.duyet],
              ['tw', t.tab.trungUong],
            ] as const
          ).map(([k, nhan]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={`tab__nut${tab === k ? ' tab__nut--dang-chon' : ''}`}
              onClick={() => datTab(k)}
            >
              {nhan}
            </button>
          ))}
        </div>
      ) : null}

      <DaiChuaCoApi canApi={canApi} />
      <p className="qt-ghi-chu">{laNhanDuyet && tab === 'duyet' ? t.ghiChuDuyet : t.ghiChuAnhXa}</p>

      <BoLocBaoCao
        tienTo={laNhanDuyet ? `bao-cao-${tab}` : 'bao-cao'}
        nam={namLoc}
        onDoiNam={(v) => doiThamSo('nam', v)}
        nutPhai={laNhanDuyet ? nutNhanDuyet : nutQuanLy}
      />

      {xuatKy.loi ? <p role="alert">{xuatKy.loi}</p> : null}
      {xuatKy.url ? (
        <p role="status">
          <a href={xuatKy.url} target="_blank" rel="noreferrer">
            {t.taiVe}
          </a>
        </p>
      ) : null}

      {moTao ? (
        <form className="hop-thoai" onSubmit={(e) => void guiTao(e)}>
          <label>
            {vi.baoCaoDinhKy.maBaoCao}
            <input value={maBaoCao} onChange={(e) => datMaBaoCao(e.target.value)} required />
          </label>
          <label>
            {vi.baoCaoDinhKy.quy}
            <input
              type="number"
              min={1}
              max={4}
              value={quy}
              onChange={(e) => datQuy(Number(e.target.value))}
              required
            />
          </label>
          <label>
            {vi.baoCaoDinhKy.nam}
            <input type="number" value={nam} onChange={(e) => datNam(Number(e.target.value))} required />
          </label>
          {loiTao ? <p role="alert">{loiTao}</p> : null}
          <button type="submit" className="nut nut--chinh" disabled={dangTao}>
            {vi.baoCaoDinhKy.tao}
          </button>
          <button type="button" className="nut" onClick={() => datMoTao(false)}>
            {vi.baoCaoDinhKy.huy}
          </button>
        </form>
      ) : null}

      <BangDuLieu<DongKyBaoCao>
        nhan={laNhanDuyet ? vi.menuCon.nhanDuyetGuiBaoCao : vi.menuCon.baoCaoDinhKy}
        cot={laNhanDuyet ? cotNhanDuyet : cotQuanLy}
        dong={dong}
        khoaDong={(d) => d.id}
        nhanDong={(d) => `${d.maBaoCao} Q${d.quy}/${d.nam}`}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={SO_DONG_MAC_DINH}
        onDoiTrang={(so) => doiThamSo('page', String(so))}
        onDoiSoDong={() => undefined}
        daChon={daChon}
        onDoiChon={datDaChon}
        dangLoc={namLoc !== ''}
        onXoaLoc={() => doiThamSo('nam', '')}
        moTaRong={vi.baoCaoDinhKy.rongMoTa}
        nhanHanhDongRong={laNhanDuyet ? undefined : vi.baoCaoDinhKy.taoKyMoi}
        onHanhDongRong={laNhanDuyet ? undefined : () => datMoTao(true)}
      />
    </div>
  );
}

/* --------------------------------- Chi tiết -------------------------------- */

export interface MH32ChiTietProps {
  api?: Pick<typeof baoCaoApi, 'chiTietKy' | 'nhapDieuChinh' | 'tongHopLai' | 'chotKy' | 'moLaiKy'>;
  apiXuat?: Pick<typeof thongKeApi, 'xuat' | 'trangThaiXuat'>;
}

export function MH32ChiTietKyBaoCao({ api, apiXuat }: MH32ChiTietProps = {}): ReactNode {
  const goi = api ?? baoCaoApi;
  const xuatKy = dungXuatKy(apiXuat ?? thongKeApi);
  const { id = '' } = useParams();
  const phien = dungPhien();
  const [moc, datMoc] = useState(0);
  const [dangHanhDong, datDangHanhDong] = useState(false);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [loiHanhDong, datLoiHanhDong] = useState<string | null>(null);
  const [suaMa, datSuaMa] = useState<string | null>(null);
  const [giaTriSua, datGiaTriSua] = useState('');
  const [lyDoSua, datLyDoSua] = useState('');
  const [moMoLai, datMoMoLai] = useState(false);
  const [lyDoMoLai, datLyDoMoLai] = useState('');

  const kq = dungTaiDuLieu<ChiTietKyBaoCao>({
    tai: (signal) => goi.chiTietKy(id, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id, moc],
  });

  const ky = kq.duLieu;
  const chiDoc = ky?.trangThai === 'da_duyet' || ky?.trangThai === 'da_gui';

  const taiLai = (): void => datMoc((x) => x + 1);

  const guiTongHop = async (): Promise<void> => {
    datDangHanhDong(true);
    datLoiHanhDong(null);
    try {
      await goi.tongHopLai(id);
      datThongBao(vi.baoCaoDinhKy.tongHopXong);
      taiLai();
    } catch (err) {
      datLoiHanhDong(err instanceof LoiApi ? err.message : 'Tổng hợp lại thất bại');
    } finally {
      datDangHanhDong(false);
    }
  };

  const guiChot = async (): Promise<void> => {
    if (!window.confirm(vi.baoCaoDinhKy.xacNhanChot)) return;
    datDangHanhDong(true);
    datLoiHanhDong(null);
    try {
      await goi.chotKy(id);
      taiLai();
    } catch (err) {
      datLoiHanhDong(err instanceof LoiApi ? err.message : 'Chốt kỳ thất bại');
    } finally {
      datDangHanhDong(false);
    }
  };

  const guiMoLai = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangHanhDong(true);
    datLoiHanhDong(null);
    try {
      await goi.moLaiKy(id, lyDoMoLai);
      datMoMoLai(false);
      datLyDoMoLai('');
      taiLai();
    } catch (err) {
      datLoiHanhDong(err instanceof LoiApi ? err.message : 'Mở lại kỳ thất bại');
    } finally {
      datDangHanhDong(false);
    }
  };

  const batDauSua = (d: DongChiTieu): void => {
    datSuaMa(d.maChiTieu);
    datGiaTriSua(String(d.giaTriDieuChinh ?? d.giaTriHeThong ?? ''));
    datLyDoSua(d.lyDoDieuChinh ?? '');
  };

  const luuSua = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!suaMa) return;
    // Lý do là BẮT BUỘC — kiểm phía giao diện, máy chủ kiểm lại (không tin cậy
    // riêng phía giao diện).
    if (lyDoSua.trim().length === 0) {
      datLoiHanhDong(vi.baoCaoDinhKy.canLyDo);
      return;
    }
    datDangHanhDong(true);
    datLoiHanhDong(null);
    try {
      await goi.nhapDieuChinh(id, [{ maChiTieu: suaMa, giaTri: Number(giaTriSua), lyDo: lyDoSua.trim() }]);
      datSuaMa(null);
      taiLai();
    } catch (err) {
      datLoiHanhDong(err instanceof LoiApi ? err.message : 'Lưu điều chỉnh thất bại');
    } finally {
      datDangHanhDong(false);
    }
  };

  const cotChiTieu = useMemo<CotBang<DongChiTieu>[]>(
    () => [
      { khoa: 'tenChiTieu', nhan: vi.baoCaoDinhKy.chiTieuCot.tenChiTieu, ve: (d) => d.tenChiTieu },
      {
        khoa: 'giaTriHeThong',
        nhan: vi.baoCaoDinhKy.chiTieuCot.giaTriHeThong,
        canPhai: true,
        // Cột hệ thống KHÔNG BAO GIỜ có ô sửa — bất biến sau điều chỉnh (cổng 5).
        ve: (d) => ((d.giaTriHeThong ?? d.nguon === 'khai_bao') ? (d.giaTriHeThong ?? '—') : '—'),
      },
      {
        khoa: 'giaTriDieuChinh',
        nhan: vi.baoCaoDinhKy.chiTieuCot.giaTriDieuChinh,
        canPhai: true,
        ve: (d) =>
          suaMa === d.maChiTieu ? (
            <form className="hang-sua" onSubmit={(e) => void luuSua(e)}>
              <input
                type="number"
                value={giaTriSua}
                onChange={(e) => datGiaTriSua(e.target.value)}
                aria-label={vi.baoCaoDinhKy.chiTieuCot.giaTriDieuChinh}
                required
              />
              <input
                value={lyDoSua}
                onChange={(e) => datLyDoSua(e.target.value)}
                placeholder={vi.baoCaoDinhKy.chiTieuCot.lyDoDieuChinh}
                aria-label={vi.baoCaoDinhKy.chiTieuCot.lyDoDieuChinh}
                required
              />
              <button type="submit" className="nut nut--nho" disabled={dangHanhDong}>
                {vi.baoCaoDinhKy.luuDieuChinh}
              </button>
              <button type="button" className="nut nut--nho" onClick={() => datSuaMa(null)}>
                {vi.baoCaoDinhKy.huyDieuChinh}
              </button>
            </form>
          ) : (
            (d.giaTriDieuChinh ?? '—')
          ),
      },
      {
        khoa: 'lyDoDieuChinh',
        nhan: vi.baoCaoDinhKy.chiTieuCot.lyDoDieuChinh,
        ve: (d) => d.lyDoDieuChinh ?? '—',
      },
      {
        khoa: 'giaTriChot',
        nhan: vi.baoCaoDinhKy.chiTieuCot.giaTriChot,
        canPhai: true,
        ve: (d) => d.giaTriChot ?? '—',
      },
      {
        khoa: 'hanhDong',
        nhan: '',
        ve: (d) =>
          !chiDoc && suaMa !== d.maChiTieu ? (
            <button type="button" className="nut nut--nho" onClick={() => batDauSua(d)}>
              {vi.baoCaoDinhKy.suaChiTieu}
            </button>
          ) : null,
      },
    ],
    [suaMa, giaTriSua, lyDoSua, dangHanhDong, chiDoc],
  );

  if (kq.trangThai === 'dang-tai') return <KhungXuong soCot={6} />;
  if (kq.trangThai === 'loi' && kq.loi) return <TrangThaiLoi loi={kq.loi} onThuLai={taiLai} />;
  if (!ky) return <TrangThaiRong moTa={vi.baoCaoDinhKy.rongMoTa} />;

  return (
    <div className="man-hinh man-hinh--bao-cao-chi-tiet">
      <header className="man-hinh__dau">
        <h1>
          {vi.baoCaoDinhKy.chiTietTieuDe}: {ky.maBaoCao} — Q{ky.quy}/{ky.nam}
        </h1>
        <Link to="/bao-cao" className="nut">
          {vi.baoCaoDinhKy.dong}
        </Link>
      </header>

      <p>
        {vi.baoCaoDinhKy.cot.trangThai}: <strong>{vi.baoCaoDinhKy.trangThai[ky.trangThai]}</strong>
        {chiDoc ? <span className="the the--canh-bao"> · {vi.baoCaoDinhKy.daKhoa}</span> : null}
      </p>

      {thongBao ? <p role="status">{thongBao}</p> : null}
      {loiHanhDong ? <p role="alert">{loiHanhDong}</p> : null}

      <div className="man-hinh__hanh-dong">
        {!chiDoc ? (
          <button type="button" className="nut" onClick={() => void guiTongHop()} disabled={dangHanhDong}>
            {dangHanhDong ? vi.baoCaoDinhKy.dangTongHop : vi.baoCaoDinhKy.tongHopLai}
          </button>
        ) : null}
        {!chiDoc ? (
          <button
            type="button"
            className="nut nut--chinh"
            onClick={() => void guiChot()}
            disabled={dangHanhDong}
          >
            {vi.baoCaoDinhKy.chotKy}
          </button>
        ) : null}
        {chiDoc && phien.coQuyen('report.reopen') ? (
          <button type="button" className="nut" onClick={() => datMoMoLai(true)}>
            {vi.baoCaoDinhKy.moLaiKy}
          </button>
        ) : null}
        {chiDoc && !phien.coQuyen('report.reopen') ? (
          <span className="the the--phu">{vi.baoCaoDinhKy.khongCoQuyenMoLai}</span>
        ) : null}
        <button type="button" className="nut" onClick={() => void xuatKy.xuat(id)} disabled={xuatKy.dangXuat}>
          {xuatKy.dangXuat ? t.dangXuat : vi.baoCaoDinhKy.xuatBieuMau}
        </button>
        {xuatKy.url ? (
          <a href={xuatKy.url} target="_blank" rel="noreferrer">
            {t.taiVe}
          </a>
        ) : null}
      </div>
      {xuatKy.loi ? <p role="alert">{xuatKy.loi}</p> : null}

      {moMoLai ? (
        <form className="hop-thoai" onSubmit={(e) => void guiMoLai(e)}>
          <label>
            {vi.baoCaoDinhKy.lyDoMoLai}
            <textarea
              value={lyDoMoLai}
              onChange={(e) => datLyDoMoLai(e.target.value)}
              required
              minLength={1}
            />
          </label>
          <button
            type="submit"
            className="nut nut--chinh"
            disabled={dangHanhDong || lyDoMoLai.trim().length === 0}
          >
            {vi.baoCaoDinhKy.moLaiKy}
          </button>
          <button type="button" className="nut" onClick={() => datMoMoLai(false)}>
            {vi.baoCaoDinhKy.huy}
          </button>
        </form>
      ) : null}

      <BangDuLieu<DongChiTieu>
        nhan={vi.baoCaoDinhKy.chiTietTieuDe}
        cot={cotChiTieu}
        dong={ky.chiTieu}
        khoaDong={(d) => d.maChiTieu}
        trangThai="co-du-lieu"
        loi={null}
        taiLau={false}
        onThuLai={taiLai}
        tong={ky.chiTieu.length}
        trang={1}
        soDongMoiTrang={Math.max(ky.chiTieu.length, 1)}
        onDoiTrang={() => undefined}
        onDoiSoDong={() => undefined}
        dangLoc={false}
        onXoaLoc={() => undefined}
      />
    </div>
  );
}
